/**
 * What a full sync does to a destination, stated wherever the user can start one: the Run button's
 * confirmation, and the edit drawer when a change to the column mappings will make the next run a
 * full one. Verbatim from the webapp (`retlWorkflow/fullSyncConfirmation.ts`), which builds both
 * from the same sentences.
 */

/**
 * Activation connections send record events, and rudder-sources sends no deletes on a full run of
 * those: it re-sends the current members and makes them the new baseline, so anyone who left the
 * audience before this run stays in the destination for good.
 */
const FULL_SYNC_CONFIRMATION =
  'This resets the sync history and sends everyone currently in the audience to the destination again. Anyone who left the audience before this run stays in the destination. A full sync does not remove them.';

/**
 * Attribute destination: a full sync writes `true` for current members and never `false` for anyone
 * who left before it, so their attribute stays `true` until they re-enter and leave again.
 */
const GAINSIGHT_PX_FULL_SYNC_SETS_TRUE =
  'sets the attribute to true in Gainsight PX for everyone currently in the audience.';
const GAINSIGHT_PX_FULL_SYNC_LEAVERS_KEEP_TRUE =
  'Anyone who left the audience before the full sync keeps true in Gainsight PX: a full sync does not set it to false, and only re-entering and then leaving the audience changes it.';

const GAINSIGHT_PX_FULL_SYNC_CONFIRMATION = `This resets the sync history and ${GAINSIGHT_PX_FULL_SYNC_SETS_TRUE} ${GAINSIGHT_PX_FULL_SYNC_LEAVERS_KEEP_TRUE}`;

/**
 * rudder-sources runs a full sync when the column mappings change, so editing the identifier column
 * has the full-sync effect above. Rows written under the old identifier values drop out of the
 * sync's history, so those users are never set to `false` either.
 */
const GAINSIGHT_PX_IDENTIFIER_CHANGE_WARNING = `Changing the identifier column makes the next run a full sync, which ${GAINSIGHT_PX_FULL_SYNC_SETS_TRUE} ${GAINSIGHT_PX_FULL_SYNC_LEAVERS_KEEP_TRUE} Users set to true under the old identifier values also keep true, because the sync no longer tracks them.`;

/**
 * rudder-sources also runs a full sync when a trait row is added or changed (trait-mappings D21), so
 * the same full-sync effect applies and every current member is written again with the new traits.
 * Removing a trait row does not trigger one.
 */
const GAINSIGHT_PX_TRAIT_CHANGE_WARNING = `Adding or changing a trait mapping makes the next run a full sync, which ${GAINSIGHT_PX_FULL_SYNC_SETS_TRUE} ${GAINSIGHT_PX_FULL_SYNC_LEAVERS_KEEP_TRUE} Every current member is written again with the new traits.`;

/** Both changed at once: the shared full-sync sentences once, then each cause's own consequence. Verbatim from the webapp. */
const GAINSIGHT_PX_IDENTIFIER_AND_TRAIT_CHANGE_WARNING = `Changing the identifier column and adding or changing a trait mapping makes the next run a full sync, which ${GAINSIGHT_PX_FULL_SYNC_SETS_TRUE} ${GAINSIGHT_PX_FULL_SYNC_LEAVERS_KEEP_TRUE} Users set to true under the old identifier values also keep true, because the sync no longer tracks them. Every current member is written again with the new traits.`;

/** Per-destination overrides, keyed by definition name; every other destination gets the generic copy. */
const FULL_SYNC_CONFIRMATION_BY_DEFINITION: Readonly<Record<string, string>> = {
  GAINSIGHT_PX_AUDIENCE: GAINSIGHT_PX_FULL_SYNC_CONFIRMATION,
};

/** Keyed like the confirmation overrides; a destination without an entry gets no warning (webapp parity). */
const IDENTIFIER_CHANGE_WARNING_BY_DEFINITION: Readonly<Record<string, string>> = {
  GAINSIGHT_PX_AUDIENCE: GAINSIGHT_PX_IDENTIFIER_CHANGE_WARNING,
};

/** Keyed like the identifier warnings; a destination without an entry gets no warning (webapp parity). */
const TRAIT_CHANGE_WARNING_BY_DEFINITION: Readonly<Record<string, string>> = {
  GAINSIGHT_PX_AUDIENCE: GAINSIGHT_PX_TRAIT_CHANGE_WARNING,
};

/** Keyed like the single-cause warnings. */
const IDENTIFIER_AND_TRAIT_CHANGE_WARNING_BY_DEFINITION: Readonly<Record<string, string>> = {
  GAINSIGHT_PX_AUDIENCE: GAINSIGHT_PX_IDENTIFIER_AND_TRAIT_CHANGE_WARNING,
};

export function fullSyncConfirmationFor(definitionName: string | null | undefined): string {
  return (definitionName ? FULL_SYNC_CONFIRMATION_BY_DEFINITION[definitionName] : undefined) ?? FULL_SYNC_CONFIRMATION;
}

/** The warning for a changed identifier column, or `null` for a destination that has none. */
export function identifierChangeWarningFor(definitionName: string | null | undefined): string | null {
  return (definitionName ? IDENTIFIER_CHANGE_WARNING_BY_DEFINITION[definitionName] : undefined) ?? null;
}

/** The warning for an added or changed trait mapping, or `null` for a destination that has none. */
export function traitChangeWarningFor(definitionName: string | null | undefined): string | null {
  return (definitionName ? TRAIT_CHANGE_WARNING_BY_DEFINITION[definitionName] : undefined) ?? null;
}

/** The warning for a changed identifier column AND an added or changed trait together, or `null`. */
export function identifierAndTraitChangeWarningFor(definitionName: string | null | undefined): string | null {
  return (definitionName ? IDENTIFIER_AND_TRAIT_CHANGE_WARNING_BY_DEFINITION[definitionName] : undefined) ?? null;
}
